import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, ModalHandle, ModalProps } from '@lfg/ui/components';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Stack, Typography } from '@mui/material';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useAccount } from 'wagmi';

import { PrimaryButton } from 'src/components/Buttons/PrimaryButton';
import { SecondaryButton } from 'src/components/Buttons/SecondaryButton';
import AdminSelect from 'src/components/NodeSaleForm/AdminSelect';
import { AdminInput } from 'src/components/NodeSaleForm/AdminTextField';
import { MemberFormInputs, MemberFormSchema } from 'src/global/schemas/member-form.schema';
import { MemberRole, MemberSchemaType } from 'src/global/schemas/member.schema';
import { colors } from 'src/global/styles/colors';
import { useCreateMemberMutation, useUpdateMemberMutation } from 'src/store/services/member.service';
import { useAppSelector } from 'src/store/utils';

export interface EditAccessModalHandle extends ModalHandle {}

export interface EditAccessModalProps extends Omit<ModalProps, 'children'> {
  projectId: string;
  member?: MemberSchemaType | null;
  onSuccess?: () => void;
}

const EditAccessModal = forwardRef<EditAccessModalHandle, EditAccessModalProps>(
  ({ projectId, member, onSuccess, ...rest }, ref) => {
    const modalRef = useRef<ModalHandle>(null);
    const { address } = useAccount();
    const { network } = useAppSelector(state => state.app);

    const [createMember, { isLoading: isCreating }] = useCreateMemberMutation();
    const [updateMember, { isLoading: isUpdating }] = useUpdateMemberMutation();

    const isLoading = isCreating || isUpdating;
    const isEditMode = !!member;

    const methods = useForm<MemberFormInputs>({
      resolver: zodResolver(MemberFormSchema),
      defaultValues: {
        name: '',
        addr: '',
        role: MemberRole.EDITOR,
      },
      mode: 'all',
      reValidateMode: 'onChange',
    });

    const onShow = useCallback(() => modalRef.current?.show(), []);
    const onHide = useCallback(() => modalRef.current?.hide(), []);

    useImperativeHandle(ref, () => ({ show: onShow, hide: onHide }), [onShow, onHide]);

    useEffect(() => {
      if (member) {
        methods.reset({
          name: member.name,
          addr: member.addr,
          role: member.role,
        });
      } else {
        methods.reset({
          name: '',
          addr: '',
          role: MemberRole.EDITOR,
        });
      }
    }, [member, methods]);

    const handleSubmit = useCallback(
      async (data: MemberFormInputs) => {
        try {
          if (isEditMode && member) {
            await updateMember({
              programId: projectId,
              memberAddr: member.addr,
              ...data,
              isActive: true,
              address: address as string,
              network,
            }).unwrap();
            toast.success('Member updated successfully');
          } else {
            await createMember({
              programId: projectId,
              ...data,
              isActive: true,
              address: address as string,
              network,
            }).unwrap();
            toast.success('Member added successfully');
          }
          onSuccess?.();
          onHide();
        } catch (error: any) {
          toast.error(error?.data?.message || `Failed to ${isEditMode ? 'update' : 'add'} member`);
        }
      },
      [isEditMode, member, projectId, address, network, updateMember, createMember, onSuccess, onHide],
    );

    const handleClose = useCallback(() => {
      methods.reset();
      onHide();
    }, [methods, onHide]);

    const roleOptions = [
      { label: 'Editor', value: MemberRole.EDITOR },
      { label: 'Admin', value: MemberRole.ADMIN },
    ];

    return (
      <Modal
        ref={modalRef}
        bgColor={colors.modal}
        sx={{
          maxWidth: '600px',
          width: '100%',
          boxShadow: '0px 6px 10px 4px rgba(0, 0, 0, 0.15), 0px 2px 3px 0px rgba(0, 0, 0, 0.30)',
          border: `1px solid ${colors.neutral015}`,
        }}
        disableScrollLock={false}
        {...rest}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleSubmit)} className='flex flex-col gap-y-6'>
            <Stack gap={3} flexGrow={1}>
              <Stack gap={2} alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Typography component={'h3'} fontSize={'18px'} fontWeight={'500'} color={'white'}>
                  {isEditMode ? 'Edit Access' : 'Add Access'}
                </Typography>
                <CloseIcon onClick={handleClose} sx={{ color: 'white', cursor: 'pointer' }} />
              </Stack>

              <Typography fontSize='14px' color={colors.white} lineHeight='20px'>
                Assign access by entering a wallet address and selecting the appropriate role.
              </Typography>

              <Stack gap={2}>
                <Stack direction={'row'} gap={2}>
                  <Box flex={1}>
                    <Typography fontSize={'14px'} fontWeight={400} color={colors.neutral300} marginBottom={1}>
                      Name
                    </Typography>
                    <AdminInput
                      {...methods.register('name')}
                      placeholder='Enter Name'
                      isError={!!methods.formState.errors.name}
                      errorMsg={methods.formState.errors.name?.message}
                    />
                  </Box>

                  <Box flex={1}>
                    <Typography fontSize={'14px'} fontWeight={400} color={colors.neutral300} marginBottom={1}>
                      Role
                    </Typography>
                    <Controller
                      name={`role`}
                      control={methods.control}
                      render={({ field }) => (
                        <AdminSelect
                          // disabled={recipientIndex === 0 || hasDeployed}
                          isError={!!methods.formState.errors.role}
                          errorMsg={methods.formState.errors.role?.message}
                          options={roleOptions}
                          {...field}
                          onChange={e => {
                            field.onChange(e);
                          }}
                        />
                      )}
                    />
                  </Box>
                </Stack>

                <Box>
                  <Typography fontSize={'14px'} fontWeight={400} color={colors.neutral300} marginBottom={1}>
                    Wallet Address
                  </Typography>
                  <AdminInput
                    {...methods.register('addr')}
                    placeholder='Enter Wallet Address'
                    // readOnly={isEditMode}
                    isError={!!methods.formState.errors.addr}
                    errorMsg={methods.formState.errors.addr?.message}
                  />
                </Box>
              </Stack>

              <Stack direction={'row'} justifyContent={'flex-end'} gap={2}>
                <SecondaryButton onClick={handleClose} disabled={isLoading}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton type='submit' isLoading={isLoading}>
                  {isEditMode ? 'Update' : 'Save'}
                </PrimaryButton>
              </Stack>
            </Stack>
          </form>
        </FormProvider>
      </Modal>
    );
  },
);

export default EditAccessModal;
